import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Helmet } from 'react-helmet-async';
import 'leaflet/dist/leaflet.css';
import './Contact.css';
import '../Animation.css';
import Loader from '../../components/Loader/Loader';
import RodoCheckbox from '../../components/legal/RodoCheckbox';
import api from '../../api/api';

// not def - niestandardowa ikona markera
const customIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/252/252025.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// dop komp - automatycznie otwiera popup
const AutoOpenPopupMarker = ({ position, icon, children }) => {
  const markerRef = useRef(null);
  const map = useMap();

  useEffect(() => {
    if (markerRef.current) {
      map.whenReady(() => {
        setTimeout(() => {
          if (markerRef.current) {
            markerRef.current.openPopup();
          }
        }, 300);
      });
    }
  }, [map]);

  return (
    <Marker position={position} icon={icon} ref={markerRef}>
      <Popup
        permanent
        closeButton={false}
        autoClose={false}
        closeOnClick={false}
        offset={[0, -10]}
      >
        {children}
      </Popup>
    </Marker>
  );
};

// komponent Toast (wyskakujące powiadomienie)
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3500); // znikanie

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <span>{message}</span>
      <button className="toast-close" onClick={onClose}>
        ×
      </button>
    </div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '',
  });
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
  const [loading, setLoading] = useState(true);
  const [rodoAccepted, setRodoAccepted] = useState(false);

  useEffect(() => {
    document.body.classList.add('contact-page');
    return () => document.body.classList.remove('contact-page');
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!rodoAccepted) {
      showToast('Musisz zaakceptować przetwarzanie danych.', 'error');
      return;
    }

    try {
      if (!window.grecaptcha) {
        showToast('Błąd zabezpieczenia reCAPTCHA. Odśwież stronę.', 'error');
        return;
      }

      const token = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'submit' }
      );

      await api.sendContactMessage({
        ...formData,
        token,
        website: '',
      });

      showToast('Wiadomość wysłana pomyślnie!', 'success');
      setFormData({ name: '', email: '', message: '', website: '' });
    } catch (error) {
      if (error.fieldErrors) {
        const messages = Object.values(error.fieldErrors).flat().join(' | ');
        showToast(messages, 'error');
      } else {
        showToast(error.message || 'Błąd wysyłania.', 'error');
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <html lang="pl" />
        <title>
          Kontakt - Portfolio Mikołaj Melnyk | Web Developer React & Full-Stack
        </title>
        <meta
          name="description"
          content="Skontaktuj się ze mną za pomocą formularza kontaktowego. Frontend & Fullstack Developer – React, Node.js, Tailwind CSS."
        />
        <meta
          name="keywords"
          content="kontakt, web developer, portfolio, React, Node.js, frontend, fullstack, Rzeszów"
        />
        <meta
          property="og:title"
          content="Kontakt - Portfolio Mikołaj Melnyk"
        />
        <meta
          property="og:description"
          content="Napisz do mnie wiadomość – chętnie porozmawiamy o projektach i współpracy."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mikolajmelnyk.pl/Contact" />
        <meta property="og:locale" content="pl_PL" />
        <meta property="og:site_name" content="Portfolio Mikołaj Melnyk" />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Mikołaj Melnyk" />
      </Helmet>

      <div className="contact-container">
        <h1 className="title_h1">Kontakt</h1>

        <div className="section-contact-form">
          <h2 className="title-contact">Formularz kontaktowy</h2>

          <p className="contact-info-text">
            Masz pytanie, pomysł na projekt, propozycję współpracy czy chcesz po
            prostu porozmawiać o kodzie?{' '}
            <strong>
              Napisz śmiało – odezwę się w najbliższym czasie!
            </strong>{' '}
          </p>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              name="website"
              value={formData.website || ''}
              onChange={handleChange}
              style={{ display: 'none' }}
              autoComplete="off"
            />

            <div className="contact-form-up">
              <div className="input-container-namesurname">
                <input
                  className="first-text"
                  type="text"
                  name="name"
                  placeholder="Imię i nazwisko"
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="input-container-email">
                <input
                  className="twice-email"
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="contact-form-down">
              <div className="input-container-message">
                <textarea
                  className="message"
                  name="message"
                  placeholder="Twoja wiadomość..."
                  value={formData.message}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                ></textarea>
              </div>

              <RodoCheckbox
                checked={rodoAccepted}
                onChange={(e) => setRodoAccepted(e.target.checked)}
              />

              <button className="send-message" type="submit">
                Wyślij wiadomość{' '}
                <i className="fas fa-arrow-right send-arrow"></i>
              </button>
            </div>
          </form>
        </div>

        <div className="section-map">
          <MapContainer
            center={[50.020179, 21.982613]}
            zoom={15}
            style={{ height: '500px', width: '100%' }}
          >
            <TileLayer
              url="https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.carto.com/">Carto</a>, <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <AutoOpenPopupMarker
              position={[50.020179, 21.982613]}
              icon={customIcon}
            >
              <p>Akademicka 3, 35-084 Rzeszów</p>
            </AutoOpenPopupMarker>
          </MapContainer>
        </div>
      </div>

      {/*  wyskakujące powiadomienie */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Contact;
